import { Button } from '@shared/components/common/Button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage
} from '@shared/components/common/Form';
import { Input } from '@shared/components/common/Input';
import { useStore } from '@shared/hooks/useStore';
import yup from '@shared/services/yup';
// import { PostForgotPasswordRequest } from '@shared/types/http-payload/auth';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowLeft } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import IconEmailSuccess from '@shared/assets/images/icon-email-success.svg';
import { ROUTES, SCREEN_TYPE } from '@shared/configs/constants';
import { REGEX_VALIDATE_EMAIL } from '@shared/components/constants/common.constants';

export default observer(function ForgotPasswordPage() {
    // variables
    const [isConfirmOk, setIsConfirmOk] = useState<boolean>(false);

    // store
    const {
        authStore: { forgotPassword }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        email: yup
            .string()
            .trim()
            .matches(REGEX_VALIDATE_EMAIL, 'メールアドレスが正しくありません。')
            .required('必須項目をご入力ください。')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            email: ''
        }
    });

    // function
    const onSubmit = async (dataForm: FormData) => {
        const body = {
            email: dataForm?.email || '',
            screenType: SCREEN_TYPE.GU
        };

        const res = await flowResult(forgotPassword(body));
        if (res) {
            setIsConfirmOk(true);
        }
    };

    return (
        <div className='container mx-auto px-4 my-10 md:my-[120px]'>
            <div className='mx-auto flex max-w-[600px] flex-col justify-center rounded-[10px] bg-[#F1F1F1] px-4 py-6 md:p-10 md:gap-8 gap-6'>
                <div className='text-center page-title-hero text-[#121212]'>パスワード再発行</div>
                {
                    isConfirmOk ?
                        (
                            <div className='space-y-6 md:space-y-8'>
                                <div className='md:gap-6 gap-4 flex flex-col items-center justify-center font-medium text-base text-[#1A1A1A]'>
                                    <img className='w-[72px]' src={IconEmailSuccess} alt='email-icon' />
                                    <div className='space-y-3 text-center text-[#121212]'>
                                        <p>メールを送信しました。</p>
                                        <p>送信メール内のURLからパスワードを設定してください。</p>
                                    </div>
                                </div>

                                <div className='h-[1px] w-full bg-[#D9D9D9]' />

                                <p className='text-center'>
                                    <Link to={ROUTES.home.href} className='inline-flex text-base items-center gap-1.5 font-medium group'>
                                        <ArrowLeft size={24} color='#343330' strokeWidth={1.25} className='transition-transform duration-500 group-hover:-translate-x-1' />
                                        ログインに戻る
                                    </Link>
                                </p>
                            </div>
                        ) :
                        (
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6 md:space-y-8'>
                                    <FormField
                                        control={form.control}
                                        name='email'
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className='leading-[21px]'>
                                                    登録メールアドレスを入力してください。
                                                </div>
                                                <FormControl>
                                                    <Input type='text' {...field} className='h-[56px] rounded-none bg-white' />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <Button
                                        type='submit'
                                        variant='default'
                                        className='w-full rounded-[10px] h-[56px] text-sm font-bold'
                                    >
                                        送信
                                    </Button>

                                    <div className='h-[1px] w-full bg-[#D9D9D9]' />

                                    <p className='text-center'>
                                        <Link to={ROUTES.home.href} className='inline-flex text-sm xsm:text-base items-center gap-1.5 font-medium group'>
                                            <ArrowLeft size={24} color='#343330' strokeWidth={1.25} className='transition-transform duration-500 group-hover:-translate-x-1' />
                                            ログインに戻る
                                        </Link>
                                    </p>
                                </form>
                            </Form>
                        )
                }
            </div>
        </div>
    );
});
